import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockOwner } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { FieldShell } from "./StockFormField";

interface OwnerPickerProps {
  id: string;
  label?: string;
  /** The chosen owner. Every stock item has one, so the picker fills this in
   * itself ("Pollards (own stock)") while it is still empty. */
  value: StockOwner | null;
  onChange: (owner: StockOwner) => void;
  error?: string;
  hint?: string;
}

/** Pick the CIS customer who owns the stock. There is no "no owner":
 * "Pollards (own stock)" is offered first and is the starting choice. */
export function OwnerPicker({
  id,
  label = "Owner",
  value,
  onChange,
  error,
  hint = "Pollards' own stock is owned by Pollards (own stock). For a customer's stock, search by customer name or code.",
}: OwnerPickerProps) {
  const dataAdapter = useDataAdapter();
  const [isChanging, setIsChanging] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [results, setResults] = useState<StockOwner[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const needsStartingChoice = value === null;

  useEffect(() => {
    if (!needsStartingChoice && !isChanging) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearchFailed(false);
    dataAdapter
      .searchStockOwners(debouncedQuery.trim())
      .then((found) => {
        if (cancelled) return;
        setResults(found);
        if (needsStartingChoice && found.length > 0) onChange(found[0]!);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("stock.owners.search_failed", {
          status: err instanceof ApiError ? err.status : undefined,
        });
        setSearchFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // onChange is a fresh closure each render; the search is keyed on the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter, debouncedQuery, isChanging, needsStartingChoice]);

  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      hint={isChanging ? hint : undefined}
    >
      {(aria) =>
        value && !isChanging ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <span>
              <span className="font-medium">{value.name}</span>
              <span className="text-muted-foreground">
                {" "}
                · {value.customer_code}
                {value.billing_account_ref
                  ? ` · purchase invoices to ${value.billing_account_ref}${
                      value.billing_account_kind === "internal"
                        ? " (internal account)"
                        : ""
                    }`
                  : " · no billing account yet"}
              </span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setQuery("");
                setIsChanging(true);
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <Input
              id={id}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Hepco Rail or CUS-K10001"
              autoComplete="off"
              {...aria}
            />
            {searchFailed && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Couldn't search customers. Please try again.
              </p>
            )}
            {results.length > 0 && (
              <ul
                aria-label="Matching customers"
                className="rounded-md border border-border divide-y divide-border max-h-48 overflow-y-auto"
              >
                {results.map((owner) => (
                  <li key={owner.customer_id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => {
                        onChange(owner);
                        setIsChanging(false);
                      }}
                    >
                      <span className="font-medium">{owner.name}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        · {owner.customer_code}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {isChanging && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsChanging(false)}
              >
                Keep {value?.name}
              </Button>
            )}
          </div>
        )
      }
    </FieldShell>
  );
}
